import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from './AuthContext';

export type WorkspaceRole = 'owner' | 'partner' | 'associate' | 'clerk';
export type WorkspaceType = 'solo' | 'firm';

export interface WorkspaceMember {
  workspaceId: string;
  userId: string;
  role: WorkspaceRole;
  status: 'active' | 'pending';
}

export interface WorkspaceData {
  firmName?: string;
  workspaceType?: WorkspaceType;
  ownerId?: string;
  [key: string]: any;
}

interface WorkspaceAccessContextValue {
  role: WorkspaceRole | null;
  workspaceData: WorkspaceData | null;
  loading: boolean;
  error: Error | null;
  isOwnerOrPartner: boolean;
  isAssociate: boolean;
  isClerk: boolean;
  canManageSettings: boolean;
  canDeleteFiles: boolean;
  isSolo: boolean;
  isFirm: boolean;
}

const WorkspaceAccessContext = createContext<WorkspaceAccessContextValue | null>(null);

export function WorkspaceAccessProvider({ children }: { children: React.ReactNode }) {
  const { user, impersonationMode, impersonatedWorkspaceId, activeWorkspaceId } = useAuth();
  const workspaceId = activeWorkspaceId;

  const [role, setRole] = useState<WorkspaceRole | null>(null);
  const [workspaceData, setWorkspaceData] = useState<WorkspaceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!user || !workspaceId) {
      setRole(null);
      setWorkspaceData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    let cancelled = false;

    void (async () => {
      try {
        const memberRef = doc(db, 'workspaces', workspaceId, 'members', user.uid);
        const workspaceRef = doc(db, 'workspaces', workspaceId);
        const [memberSnap, workspaceSnap] = await Promise.all([
          getDoc(memberRef),
          getDoc(workspaceRef),
        ]);

        if (cancelled) return;

        if (memberSnap.exists()) {
          const data = memberSnap.data() as WorkspaceMember;
          setRole(data.status === 'active' ? data.role : null);
        } else {
          setRole(null);
        }

        if (workspaceSnap.exists()) {
          setWorkspaceData(workspaceSnap.data() as WorkspaceData);
        } else {
          setWorkspaceData(null);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, workspaceId]);

  const isImpersonatingGod = impersonationMode === 'god' && impersonatedWorkspaceId === workspaceId;
  const isGod = isImpersonatingGod;

  const value = {
    role,
    workspaceData,
    loading,
    error,
    isOwnerOrPartner: isGod || role === 'owner' || role === 'partner',
    isAssociate: role === 'associate',
    isClerk: role === 'clerk',
    canManageSettings: isGod || role === 'owner' || role === 'partner',
    canDeleteFiles: isGod || role === 'owner' || role === 'partner' || role === 'associate',
    isSolo: workspaceData?.workspaceType === 'solo',
    isFirm: workspaceData?.workspaceType === 'firm',
  };

  return (
    <WorkspaceAccessContext.Provider value={value}>
      {children}
    </WorkspaceAccessContext.Provider>
  );
}

export function useWorkspaceAccess() {
  const context = useContext(WorkspaceAccessContext);
  if (!context) {
    throw new Error('useWorkspaceAccess must be used within a WorkspaceAccessProvider');
  }
  return context;
}
